import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 500;

function modelFilterText(model: any): string {
  const value = model?.filter;
  return value == null ? '' : String(value);
}

const DebouncedTextFloatingFilter = forwardRef((props: any, ref) => {
  const [value, setValue] = useState(() => modelFilterText(props.currentParentModel?.()));
  const timerRef = useRef<number | undefined>();
  // Last filter text known to the parent filter (synced from it or applied by us).
  const lastSyncedTextRef = useRef(value);
  const propsRef = useRef(props);
  propsRef.current = props;

  useImperativeHandle(ref, () => ({
    onParentModelChanged(parentModel: any, event?: { afterFloatingFilter?: boolean }) {
      if (event?.afterFloatingFilter) return;
      const text = modelFilterText(parentModel);
      // AG Grid re-sends the unchanged model for every active filter on row data updates, on
      // other columns' filter changes and after our own apply (without `afterFloatingFilter`).
      // While the user is typing (apply pending), ignore those so the typed text and its
      // pending apply are kept. A real model change (e.g. restore / reset) still syncs.
      if (timerRef.current !== undefined && text === lastSyncedTextRef.current) return;
      lastSyncedTextRef.current = text;
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
      setValue(text);
    },
  }));

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const applyValue = (next: string) => {
    propsRef.current.parentFilterInstance?.((instance: any) => {
      const isNumber = instance?.getFilterType?.() === 'number';
      const type = isNumber ? 'equals' : 'contains';
      if (!next) {
        lastSyncedTextRef.current = '';
        instance.onFloatingFilterChanged(type, null);
        return;
      }
      if (isNumber) {
        const parsed = Number(next);
        if (Number.isNaN(parsed)) return;
        lastSyncedTextRef.current = String(parsed);
        instance.onFloatingFilterChanged(type, parsed);
        return;
      }
      lastSyncedTextRef.current = next;
      instance.onFloatingFilterChanged(type, next);
    });
  };

  return (
    <div className="ag-floating-filter-input" role="presentation">
      <div className="ag-text-field ag-input-field">
        <div role="presentation">
          <div className="ag-input-field-label ag-text-field-label" />
          <div className="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper" role="presentation">
            <input
              className="ag-input-field-input ag-text-field-input"
              type="text"
              value={value}
              disabled={Boolean(props.filterParams?.readOnly)}
              onChange={(event) => {
                const next = event.target.value;
                setValue(next);
                window.clearTimeout(timerRef.current);
                const debounceMs = props.filterParams?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
                timerRef.current = window.setTimeout(() => {
                  timerRef.current = undefined;
                  applyValue(next);
                }, debounceMs);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

DebouncedTextFloatingFilter.displayName = 'DebouncedTextFloatingFilter';

export { DebouncedTextFloatingFilter };